import 'isomorphic-unfetch';
import * as React from 'react';
import {Formik} from 'formik';
import ReCAPTCHA from "react-google-recaptcha";
import { Flex, Box } from 'reflexbox'
import { required, email } from '../../utils/validations';
import {
  Label,
  Button,
  Input,
  Textarea,
  Error,
  Success,
  Loader as LoaderStyle,
  Form
} from '../../components/forms/styled/form'
import Loader from '../../components/Icons/Loader'
import Checkbox from "../../components/forms/Checkbox";
import { useRef } from 'react';
import {Translate, TranslateSwitch} from '../../components/tr';

interface FormProps {
  recaptchaKey: string;
}

const ContactForm = ({
  recaptchaKey,
}: FormProps) => {
  const recaptchaRef = useRef(null);

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const response = await fetch('/api/send-email', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        setStatus({ msg: 'Email has been successfully sent, thank you!' });
      } else {
        setStatus({ msg: await response.text() });
      }
    } catch (e) {
      // @ts-expect-error
      setStatus({ msg: e.message });
    }

    setSubmitting(false);
  };

  const validate = (values) => {
    const errors = {
      email: required(values.email) || email(values.email),
      nickname: required(values.nickname),
      message: required(values.message),
      paintingType: required(values.paintingType),
      recaptchaValue: required(values.recaptchaValue, 'Please confirm you\'re not robot :)'),
    };

    return JSON.parse(JSON.stringify(errors));
  };

  return (
    <Formik
      initialValues={{
        email: '',
        nickname: '',
        paintingType: ['oil'],
        message: '',
        recaptchaValue: '',
      }}
      onSubmit={onSubmit}
      validate={validate}
    >
      {props => (
        <Form onSubmit={props.handleSubmit}>
          <Label htmlFor="email">
            <Translate text={'E-mail'} />
          </Label>

          {props.errors.email && props.touched.email &&
          <Error>{props.errors.email}</Error>}
          <Input
            type="text"
            id="email"
            name="email"
            placeholder="Enter your email"
            onChange={props.handleChange}
            onBlur={props.handleBlur}
          />

          <Label htmlFor="nickname">
            <Translate text={'Your name'} />
          </Label>

          {props.errors.nickname && props.touched.nickname &&
          <Error>{props.errors.nickname}</Error>}

          <Input
            type="text"
            id="nickname"
            name="nickname"
            placeholder="Enter your name"
            onChange={props.handleChange}
            onBlur={props.handleBlur}
          />

          <Label htmlFor="paintingType">
            <Translate text={'Choose technique of painting'} />
          </Label>
          <p style={{ marginTop: 5}}>
            ? --{'>'}<a href={'/blog/rozdily-technik/'} target="_blank" rel="noopener norefferer"><Translate text={'Show me difference between painting mediums'} /></a>
          </p>
          {props.errors.paintingType && props.touched.paintingType &&
          <Error>{props.errors.paintingType}</Error>}

          <Flex flexWrap={'wrap'} my={4} flexDirection={['column', 'column', 'row', 'row', 'row']}>
            <Box width={[1,1,1/3]} flex={['auto', 'auto', 1]}>
              <Checkbox
                id={'oil'}
                value={'oil'}
                name="paintingType"
                label={'Oil painting'}
                price={false}
              />
            </Box>

            <Box width={[1,1,1/3]} flex={['auto', 'auto', 1]}>
              <Checkbox
                id={'watercolor'}
                value={'watercolor'}
                name="paintingType"
                label={'Watercolor painting'}
                price={false}
              />
            </Box>

            <Box width={[1,1,1/3]} flex={['auto', 'auto', 1]}>
              <Checkbox
                id={'not-specified'}
                value={'not-specified'}
                name="paintingType"
                label={'I don\'t know yet'}
                price={false}
              />
            </Box>
          </Flex>


          <Label htmlFor="message">
            <Translate text={'Describe your idea'} />
          </Label>
          <small>? --{'>'}
            <TranslateSwitch
              en={(
                <>
                  You can introduce whatever what expresses you or what you are interested in. They can be your cultural idols, books or stories which you are connected with. Which feelings should be visible in the picture? What colors do you like? Whatever is important to you just write me. The more information you provide the closer we get to your image.
                </>
              )}
              cs={(
                <>
                  Můžeš uvést cokoliv, co tě vyjadřuje, co tě zajímá, tvé kulturní vzory, knihy, příběhy, které se tě dotýkají. Jaké pocity by z obrazu měly vyzařovat? Jaké máš rád/a barvy? Cokoliv, co je pro tebe důležité, můžeš zmínit. Čím více informací mi poskytneš, tím blíže se dostaneme k tvé představě.
                </>
              )}
            />
          </small>

          {props.errors.message && props.touched.message &&
          <Error>{props.errors.message}</Error>}
          <Textarea
            id="message"
            name="message"
            placeholder="Enter message"
            onChange={props.handleChange}
            onBlur={props.handleBlur}
          />

          {props.status && props.status.captchaError && <Error>{props.status.captchaError}</Error>}

          <Flex flexWrap={'wrap'} justifyContent={'center'}>
            <Box width={[1]}>
              {props.errors.recaptchaValue && props.touched.recaptchaValue &&
              <Error className="input-feedback">{props.errors.recaptchaValue}</Error>}
              <ReCAPTCHA
                name="recaptchaValue"
                sitekey={recaptchaKey}
                ref={recaptchaRef}
                onChange={(value) => props.setFieldValue("recaptchaValue", value)}
              />
            </Box>

            {props.status && props.status.msg && <Success>{props.status.msg}</Success>}

            <Box width={[1]} py={[3]} style={{ textAlign: 'center'}}>
              <Button
                type="submit"
                disabled={props.isSubmitting}
              >
                {props.isSubmitting && <span><LoaderStyle><Loader/></LoaderStyle>Loading</span>}
                {!props.isSubmitting && <Translate text={'Make it happen'} />}
              </Button>
            </Box>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};

export default ContactForm;
